'use client';
import Button from 'components/buttons/Button';
import { FormattedInputChoice } from 'types/kontentAi';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import { RootState } from 'lib/store';
import { SetStateAction } from 'react';
import { Dispatch } from 'react';
import { usePathname } from 'next/navigation';
import { incrementMedicareEducationCompletionCount } from 'actions/Database';
import { setUserDatabase } from 'slices/userSlice';
import { selectUserDynamicValues } from 'slices/dynamicDataSelectors';
import { setDynamicValues } from 'slices/dynamicSlice';
import { UserDynamicValues } from 'types/user';
import { filterChoices } from 'utils/conditionsCheck';
import { contentWithData } from 'utils/content';
import { selectAudioSource } from 'utils/mediaUtils';

interface ChoicesProps {
  choices: FormattedInputChoice[];
  onContinue: () => void;
  setCurrentAudioSource: Dispatch<SetStateAction<string>>;
  setCurrentAudioType: Dispatch<SetStateAction<'bridge' | 'response' | 'step'>>;
  setCurrentAudioCaptions: Dispatch<SetStateAction<string>>;
}

const Choices = ({
  choices,
  onContinue,
  setCurrentAudioSource,
  setCurrentAudioType,
  setCurrentAudioCaptions,
}: ChoicesProps) => {
  const pathname = usePathname();
  const userData = useAppSelector((state: RootState) =>
    selectUserDynamicValues(state)
  ) as UserDynamicValues;
  const dispatch = useAppDispatch();

  const filteredChoices = filterChoices(choices, userData as UserDynamicValues);

  return (
    <>
      {filteredChoices.map((choice, index) => (
        <Button
          key={`${choice}-${index}`}
          className="button choice"
          data-testid={`button-choice-${index}`}
          onClick={async (
            event: React.MouseEvent<HTMLButtonElement, MouseEvent>
          ) => {
            event.preventDefault();

            dispatch(
              setDynamicValues({
                value: choice.codename,
                variableName: 'lastButtonClicked',
              })
            );

            if (choice.value && choice.variableName) {
              const value = contentWithData(choice.value, userData);
              dispatch(
                setDynamicValues({
                  value: value as string,
                  variableName: choice.variableName,
                })
              );
            }

            if (pathname === '/ec') {
              try {
                const updated =
                  await incrementMedicareEducationCompletionCount();
                dispatch(
                  setUserDatabase({
                    ...updated,
                    created_at: null,
                    updated_at: null,
                  })
                );
              } catch {
                // Not signed in or persistence unavailable; continue navigation
              }
            }

            const responseAudioUrl = selectAudioSource(choice.responseAudio);
            const closedCaptions =
              choice.responseAudio?.closedCaption?.url || '';

            // set the click button code name to the state

            if (!responseAudioUrl) {
              // Page-level response audio (or navigation) is handled by
              // onContinue. Writing an empty source afterward unmounts the
              // player before that clip can finish, so the member never advances.
              onContinue();
              return;
            }

            setCurrentAudioSource!(responseAudioUrl);
            setCurrentAudioType!('response');
            setCurrentAudioCaptions!(closedCaptions);
          }}
          size="full-width"
          title={contentWithData(choice.label, userData) as string}
          variant="filled"
        />
      ))}
    </>
  );
};

export default Choices;
